// get the url based on the plotform

import { Platform } from "react-native";

/**
* Dynamically gets the BFF Web URL based on the current browser location
* For web: extracts domain from browser address bar and appends /BFF_Web
* For mobile: uses environment variables or defaults
*/
const getBffWebUrl = (): string => {
  // For web platform, dynamically get URL from browser
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    try {
      // Get the current URL from the browser's address bar
      const currentUrl = window.location.href;
      const url = new URL(currentUrl);

      // For localhost, return the original localhost URL
      if (url.hostname === 'localhost' || url.hostname === '127.0.0.1') {
        return 'http://localhost:8080';
      }

      if (url.hostname === '10.0.2.2') {
        return 'http://10.0.2.2:8080';
      }

      const protocol = url.protocol;
      const hostnameParts = url.hostname.split('.');

      // Handle edge case where hostname has no dots (single part)
      if (hostnameParts.length < 2) {
        return `https://${url.hostname}/quickstage-bff-web`;
      }

      const subdomain = hostnameParts[0];
      const domain = hostnameParts.slice(1).join('.');

      // Map moneygram.com domains to AWS domains
      if (domain === 'moneygram.com') {
        return `${protocol}//${subdomain}.moneygram.com/quickstage-bff-web`;
      }
      // // For external domains, extract domain and append /BFF_Web
      // const domain = url.hostname;
      // const protocol = url.protocol;
      // // const port = url.port ? `:${url.port}` : '';
      // console.log('domain', protocol, domain);

      return `https://${url.hostname}/quickstage-bff-web`;
    } catch (error) {
      console.warn('Failed to parse browser URL, falling back to default:', error);
      return 'http://localhost:8080';
    }
  }

  // For React Native (iOS/Android), use environment variables or defaults
  if (Platform.OS === 'ios' || Platform.OS === 'android') {
    // In development, try to connect to the development server
    if (__DEV__) {
      // For iOS simulator, localhost works
      if (Platform.OS === 'ios') {
        return process.env.REACT_APP_BFF_WEB_URL || 'http://localhost:8080';
      }
      // For Android, we need to use the machine's IP address
      return process.env.REACT_APP_BFF_WEB_URL || 'http://10.0.2.2:8080';
    }

    // In production, use the production API URL
    return process.env.REACT_APP_BFF_WEB_URL || 'https://d2agentworks.aws.moneygram.com/quickstage-bff-web';
  }

  // Fallback
  return 'http://localhost:8080';
};

/**
 * Base URL for MAPI (`/api/mapi/*`). The 1.0 transactional service was retired and the BFF
 * no longer mounts these handlers, so `REACT_APP_TRANSACTIONAL_SERVICE_URL` must point at a
 * host that serves MAPI. Falls back to the BFF origin, where these routes now 404.
 */
const getTransactionalServiceUrl = (): string => {
  const fromEnv =
    typeof process !== "undefined" && process.env?.REACT_APP_TRANSACTIONAL_SERVICE_URL;
  if (fromEnv && String(fromEnv).trim()) {
    return String(fromEnv).replace(/\/$/, "");
  }

  return getBffWebUrl();
};

/**
 * Full URL for transactional-service routes (same path suffixes as BFF where applicable).
 */
export const getTransactionalApiUrl = (endpoint: string): string => {
  return `${getTransactionalServiceUrl()}${endpoint}`;
};

export const API_ENDPOINTS = {
  // API endpoints
  //LOAD_COUNTRIES: '/api/country',
  LOAD_COUNTRIES: '/api/common/country',
  GET_LOCATION_BY_KID: '/api/common/location',
  FEE_LOOKUP_INIT: '/api/send/FeeLookup',
  CONSUMER_LOOKUP: '/api/common/ConsumerLookup',
  CONSUMER_LOOKUP_BILLPAY: '/api/billpay/ConsumerLookup',
  AUTH_CONSUMER: '/api/send/AuthConsumer',
  GET_FIELDS_FOR_PRODUCT: '/api/send/getFieldsForProductRequest',
  UPDATE_CONSUMER_PROFILE: '/api/send/updateConsumerProfile',
  VALIDATE_CONSUMER: '/api/send/validateConsumer',
  POST_TRANSACTION_TO_KIOSK: '/api/common/postTransactionToKiosk',
  REFERENCE_NUMBER_SEARCH: '/api/receive/ReferenceNumberSearch',
  GET_FIELDS_FOR_PRODUCT_FOR_RECEIVE: '/api/receive/getFieldsForProductRequest',
  VALIDATE_CONSUMER_FOR_RECEIVE: '/api/receive/validateConsumer',
  BILLER_SEARCH: '/api/billersearch/PostByName',
  BILLPAY_FEE_LOOKUP: '/api/billpay/FeeLookup',
  GET_FIELDS_FOR_PRODUCT_FOR_BILLPAY: '/api/billpay/getFieldsForProductRequest',
  VALIDATE_BILLPAY: '/api/billpay/validateBillPay',
  LOCATION_SUGGESTIONS: '/api/location/suggestions',
  LOCATION_PLACES: '/api/location/places',
  MAPI_PROFILE_SEARCH: '/api/mapi/profileSearch',
  MAPI_QUOTE: '/api/mapi/quote',
  MAPI_TRANSACTION_FIELDS_SEND: '/api/mapi/transactionFieldsSend',
  MAPI_COUNTRIES: '/api/mapi/countries',
  MAPI_COUNTRY_DETAILS: '/api/mapi/countries',
  MAPI_CREATE_PROFILE: '/api/mapi/createProfile',
  MAPI_UPDATE_TRANSACTION: '/api/mapi/updateTransaction',
}

/**
* Gets the API URL for the current platform and environment
*/
export const getApiUrl = (endpoint: string): string => {
  return `${getBffWebUrl()}${endpoint}`;
 // return getBffWebUrl();
};

export const AmountLimits = {
  SEND_LIMIT: 9100,
  BILLPAY_LIMIT: 9100, 
  RECEIVE_LIMIT: 500,
}

export const version = 'NOV.25.2025';

/**
 * PostHog Configuration
 * Simple configuration with prod and non-prod API keys
 */
export const getPostHogConfig = () => {
  let isProduction = false;

  // For web platform, check browser location
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    try {
      const currentUrl = window.location.href;
      const url = new URL(currentUrl);

      const nonProdEnvironments = ["localhost", "127.0.0.1", "10.0.2.2", "d3", "d5", "q3", "q5", "q1", "ext"];

      // Check if hostname matches any non-prod environment
      const isNonProdHostname = nonProdEnvironments.some(env => {
        if (env === 'localhost' || env === '127.0.0.1' || env === '10.0.2.2') {
          return url.hostname === env;
        }
        // For other environments, check if hostname contains the environment name
        return url.hostname.includes(env) && url.hostname.includes('quickstage');
      });

      isProduction = !isNonProdHostname;
    } catch (error) {
      // Fallback to __DEV__ for web
      isProduction = !__DEV__;
    }
  } else {
    // For React Native (iOS/Android), use __DEV__
    isProduction = !__DEV__;
  }

  return {
    apiKey: isProduction
      ? 'phc_deC1ImSrw8U7NKIllodHbcRZ2GDXFqVctNSvVXpuwAD'
      : 'phc_JySI7bxWr0WiCCcYnMHisyw37xAInq09DqoroB5Xze4',
    apiHost: 'https://us.i.posthog.com',
    environment: isProduction ? 'production' : 'development',
    isDevelopment: !isProduction
  };
};

 
